import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, X, Check, ChevronDown, AlertCircle } from "lucide-react";

/**
 * Searchable dropdown of ShipStation stores.
 * Loads /api/admin/shipstation/stores once on mount, then lets the admin
 * filter by store name / marketplace and pick one (or clear).
 */
export default function ShipStationStoreSelect({ value, onChange, multiple = false, label = "ShipStation Store", testId = "shipstation-store-select" }) {
  const values = multiple ? (Array.isArray(value) ? value.map(String) : []) : [];
  const [stores, setStores] = useState(null); // null = loading, [] = none, array = loaded
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/shipstation/stores");
        if (!cancelled) setStores(data.stores || []);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not load stores");
          setStores([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // close on outside click
  useEffect(() => {
    function onClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selected = useMemo(() => {
    if (multiple || !value || !stores) return null;
    return stores.find((s) => s.store_id === String(value));
  }, [value, stores, multiple]);

  const selectedMulti = useMemo(
    () => (stores || []).filter((s) => values.includes(s.store_id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stores, value]
  );

  const toggle = (id) => {
    if (!multiple) { onChange(id); setOpen(false); setQuery(""); return; }
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);
  };

  const filtered = useMemo(() => {
    if (!stores) return [];
    if (!query) return stores;
    const q = query.toLowerCase();
    return stores.filter(
      (s) =>
        (s.store_name || "").toLowerCase().includes(q) ||
        (s.marketplace_name || "").toLowerCase().includes(q) ||
        s.store_id.includes(q)
    );
  }, [stores, query]);

  let triggerLabel;
  if (multiple) {
    triggerLabel = values.length === 0
      ? <span className="text-slate-400">No stores linked — click to choose</span>
      : (
        <span className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
          {(selectedMulti.length ? selectedMulti : values.map((v) => ({ store_id: v, store_name: `Store ${v}` }))).map((s) => (
            <span key={s.store_id} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
              {s.store_name}
            </span>
          ))}
        </span>
      );
  } else if (selected) {
    triggerLabel = (
      <span className="flex items-center gap-2 min-w-0 flex-1">
        <span className="font-semibold text-slate-900 truncate">{selected.store_name}</span>
        <span className="text-xs text-slate-500 shrink-0">· {selected.marketplace_name}</span>
        <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded shrink-0">{selected.store_id}</code>
      </span>
    );
  } else if (value) {
    triggerLabel = <span className="text-slate-700 text-sm">Store ID: <code className="bg-slate-100 px-1 rounded">{value}</code></span>;
  } else if (stores === null) {
    triggerLabel = <span className="text-slate-400">Loading stores…</span>;
  } else {
    triggerLabel = <span className="text-slate-400">No store linked — click to choose</span>;
  }

  return (
    <div className="md:col-span-2" ref={wrapperRef}>
      <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">
        {label}
      </label>

      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-slate-300 rounded-md bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-left"
      >
        {triggerLabel}
        <div className="flex items-center gap-1 shrink-0">
          {!multiple && value && (
            <span
              data-testid={`${testId}-clear`}
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange(""); } }}
              className="text-slate-400 hover:text-red-600 rounded p-0.5 cursor-pointer"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {error && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}

      {open && stores && (
        <div className="relative">
          <div data-testid={`${testId}-dropdown`} className="absolute z-50 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
            <div className="border-b border-slate-100 px-3 py-2 flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                autoFocus
                data-testid={`${testId}-search`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${stores.length} stores…`}
                className="flex-1 text-sm outline-none bg-transparent placeholder:text-slate-400"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <ul className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-slate-400">No stores match.</li>
              ) : (
                filtered.map((s) => {
                  const isSelected = multiple ? values.includes(s.store_id) : s.store_id === String(value);
                  return (
                    <li key={s.store_id}>
                      <button
                        type="button"
                        data-testid={`${testId}-option-${s.store_id}`}
                        onClick={() => toggle(s.store_id)}
                        className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 transition-colors ${
                          isSelected ? "bg-blue-50/60" : ""
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 text-sm">{s.store_name}</span>
                            <span className="text-xs text-slate-500">· {s.marketplace_name}</span>
                            {!s.active && <span className="text-[10px] uppercase bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Inactive</span>}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">Store ID: {s.store_id}{s.account_name ? ` · ${s.account_name}` : ""}</div>
                        </div>
                        {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
